import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Upload, FileText, Trash2, Loader2 } from 'lucide-react';
import { safeConfirm } from '../lib/confirm';

/**
 * Inline medical document uploader — used inside Part 1 intake so the patient
 * can attach referrals, prior imaging, lab reports while filling the subjective.
 * Documents save to the existing /api/documents endpoints scoped to patient_id.
 */
export default function MedicalDocsUpload({ patientId, isStaff }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('referral');

  // Iter 69 — backend exposes GET /documents?patient_id=..., not /documents/{pid}.
  // The path-style variant was bouncing as 404/405 and showed up as the
  // mysterious console noise reported by the iter_68 test agent.
  const load = () => api.get('/documents', { params: { patient_id: patientId } }).then(r => setDocs(r.data)).catch(()=>{});
  useEffect(() => { if (patientId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [patientId]);

  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('patient_id', patientId);
      fd.append('document_type', docType);
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (e) {
      alert('Upload failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const remove = async (id) => {
    if (!await safeConfirm('Delete this document?')) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  return (
    <div className="bento-card print:p-3" data-testid="intake-docs">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="w-4 h-4 text-brand"/>
        <h3 className="font-heading font-bold">Medical documentation</h3>
      </div>
      <p className="text-xs text-fg-muted mb-3">
        Upload referrals, lab reports, MRI/X-ray summaries, prior treatment notes. Encrypted and HIPAA / GDPR compliant.
      </p>
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <select className="input max-w-[220px] text-sm" value={docType}
                onChange={e=>setDocType(e.target.value)} data-testid="doc-type">
          <option value="referral">Referral letter</option>
          <option value="imaging">Imaging (X-ray / MRI / CT / US)</option>
          <option value="lab">Lab / blood work</option>
          <option value="prior_treatment">Prior treatment notes</option>
          <option value="insurance">Insurance / authorization</option>
          <option value="other">Other</option>
        </select>
        <label className={`btn-secondary inline-flex items-center gap-2 cursor-pointer text-sm ${uploading?'opacity-60 cursor-wait':''}`}
               data-testid="upload-doc-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
          {uploading ? 'Uploading…' : 'Upload file'}
          <input type="file" hidden onChange={onPick}
                 accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx,.txt"/>
        </label>
        <span className="text-[11px] text-fg-muted">Up to 25 MB · PDF, image, or doc</span>
      </div>

      {docs.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {docs.map(d => (
            <li key={d.document_id} className="flex items-center gap-2 text-sm border-b border-soft py-1.5"
                data-testid={`doc-row-${d.document_id}`}>
              <FileText className="w-3.5 h-3.5 text-fg-muted shrink-0"/>
              <a href={d.download_url || `/api/documents/download/${d.document_id}`} target="_blank"
                 rel="noopener noreferrer" className="text-brand hover:underline truncate">
                {d.original_name || d.filename}
              </a>
              <span className="text-[10px] uppercase tracking-wider text-fg-muted ml-auto shrink-0">{d.document_type}</span>
              {(isStaff || d.uploaded_by === patientId) && (
                <button onClick={()=>remove(d.document_id)} className="text-[#B83A3A] shrink-0 print:hidden" data-testid={`doc-del-${d.document_id}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
